import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { 
    Search, 
    Plus, 
    Minus, 
    Trash2, 
    ShoppingCart,
    CreditCard,
    Banknote,
    QrCode,
    Loader2,
    AlertTriangle,
    Gift,
    Wine,
    Store,
    ScanBarcode
} from 'lucide-react';
import { toast } from 'sonner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Todos', 'Comida', 'Cerveja', 'Refrigerante', 'Mercearia', 'Destilados', 'Vinhos', 'Combos', 'Dose Pronta', 'Gelo', 'Tabacaria e Cigarro', 'Energéticos'];

const PDVPage = () => {
    const [products, setProducts] = useState([]);
    const [combos, setCombos] = useState([]);
    const [cart, setCart] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('Todos');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [mobileCartOpen, setMobileCartOpen] = useState(false);
    
    // Cash register state
    const [cashRegister, setCashRegister] = useState(null);
    const [cashRegisterLoading, setCashRegisterLoading] = useState(true);
    
    // Dialog para escolher tipo de venda (dose/garrafa)
    const [doseDialogOpen, setDoseDialogOpen] = useState(false);
    const [doseProduct, setDoseProduct] = useState(null);
    const [selectedSaleType, setSelectedSaleType] = useState('dose');
    
    // Dialog para produtos compostos com opções (ex: Gin de 10)
    const [compositeOptionDialogOpen, setCompositeOptionDialogOpen] = useState(false);
    const [compositeOptionProduct, setCompositeOptionProduct] = useState(null);
    const [compositeSelections, setCompositeSelections] = useState({}); // { groupName: { product_id, product_name } }
    
    // Checkout form
    const [paymentMethod, setPaymentMethod] = useState('dinheiro');
    const [saleOrigin, setSaleOrigin] = useState('loja_fisica'); // 'loja_fisica'
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [discount, setDiscount] = useState(0);
    // Cash payment fields
    const [amountPaid, setAmountPaid] = useState('');
    const [changeGiven, setChangeGiven] = useState(0);
    
    // Pagamento dividido
    const [splitPaymentMode, setSplitPaymentMode] = useState(false);
    const [payments, setPayments] = useState([]); // Lista de pagamentos: [{method, amount}]
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState('dinheiro');
    const [currentPaymentAmount, setCurrentPaymentAmount] = useState('');
    
    // Scanner state
    const [scannerEnabled, setScannerEnabled] = useState(true);

    // Calculate totals first (needed by useEffect below)
    const subtotal = cart.reduce((sum, item) => sum + item.total_price, 0);
    const total = Math.max(0, subtotal - discount);
    
    // Calcular total já pago e restante
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const remaining = Math.max(0, total - totalPaid);
    
    // Função auxiliar para adicionar produto ao carrinho via barcode
    const addToCartByBarcode = useCallback((product) => {
        // Determinar preço atual (promocional ou normal)
        const currentPrice = product.on_promotion && product.promotional_price 
            ? product.promotional_price 
            : product.price;
        
        const itemId = product.id;
        const existingItem = cart.find(item => item.product_id === itemId);
        
        if (existingItem) {
            if (existingItem.quantity >= product.stock_quantity) {
                toast.error('Estoque insuficiente');
                return;
            }
            setCart(prevCart => prevCart.map(item => 
                item.product_id === itemId
                    ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
                    : item
            ));
        } else {
            setCart(prevCart => [...prevCart, {
                product_id: itemId,
                product_name: product.name,
                quantity: 1,
                unit_price: currentPrice,
                total_price: currentPrice,
                max_stock: product.stock_quantity,
                is_combo: false,
                combo_id: null,
                combo_items: null,
                is_composite: false,
                composite_id: null,
                composite_ingredients: null,
                on_promotion: product.on_promotion || false,
                original_price: product.price
            }]);
        }
        toast.success(`${product.name} adicionado`, {
            icon: '✅',
            duration: 1500
        });
    }, [cart]);
    
    // Handler para leitura de código de barras no PDV
    const handleBarcodeScanPDV = useCallback(async (barcode) => {
        console.log('[PDV] Scanner detectou código:', barcode);
        console.log('[PDV] Caracteres do código:', barcode.split('').map(c => `${c}(${c.charCodeAt(0)})`).join(' '));
        
        // Limpar código - remover caracteres não numéricos se o código parecer ser EAN/UPC
        let cleanBarcode = barcode;
        
        // Se o código tem mais de 8 dígitos e começa com letra, pode ser erro do scanner
        // Tentar extrair apenas números
        if (barcode.length >= 8 && /^[A-Za-z]/.test(barcode)) {
            const numbersOnly = barcode.replace(/[^0-9]/g, '');
            if (numbersOnly.length >= 8) {
                console.log('[PDV] Código parece ter prefixo inválido, usando apenas números:', numbersOnly);
                cleanBarcode = numbersOnly;
            }
        }
        
        // Desabilitar scanner temporariamente
        setScannerEnabled(false);
        
        try {
            // Buscar produto pelo código de barras
            console.log('[PDV] Buscando produto no backend com código:', cleanBarcode);
            const response = await axios.get(`${API_URL}/api/products/barcode/${cleanBarcode}`);
            const product = response.data;
            console.log('[PDV] Produto encontrado:', product.name);
            
            // Verificar se o produto está ativo
            if (product.active === false) {
                toast.error('Produto inativo!');
                return;
            }
            
            // Verificar estoque
            if (product.stock_quantity <= 0 && (!product.sold_by_dose || product.open_bottle_doses_remaining <= 0)) {
                toast.error('Produto sem estoque!');
                return;
            }
            
            // Adicionar ao carrinho usando a função existente
            // Se for produto por dose, abrir dialog
            if (product.sold_by_dose) {
                setDoseProduct(product);
                setSelectedSaleType('dose');
                setDoseDialogOpen(true);
                toast.success(`${product.name} - Selecione tipo`, {
                    icon: '🥃',
                    duration: 2000
                });
            } else {
                // Adicionar direto ao carrinho
                addToCartByBarcode(product);
            }
            
        } catch (error) {
            console.error('[PDV] Erro ao buscar produto:', error);
            if (error.response?.status === 404) {
                // Se não encontrou, mostrar o código original para debug
                toast.error(`Produto não encontrado: ${barcode}`, {
                    description: cleanBarcode !== barcode ? `Código limpo: ${cleanBarcode}` : undefined,
                    icon: '❌',
                    duration: 5000
                });
            } else {
                toast.error('Erro ao buscar produto');
            }
        } finally {
            // Reativar scanner após um delay
            setTimeout(() => setScannerEnabled(true), 500);
        }
    }, [addToCartByBarcode]);

    // Hook do scanner - ativo apenas quando dialogs estão fechados
    const { isScanning } = useBarcodeScanner({
        enabled: scannerEnabled && !checkoutOpen && !doseDialogOpen && !compositeOptionDialogOpen && !mobileCartOpen,
        onScan: handleBarcodeScanPDV,
        minLength: 4,
        maxDelay: 150  // Aumentado para compatibilidade com scanners mais lentos
    });

    // Calculate change when amountPaid changes
    useEffect(() => {
        if (paymentMethod === 'dinheiro' && amountPaid) {
            const paid = parseFloat(amountPaid) || 0;
            const change = Math.max(0, paid - total);
            setChangeGiven(change);
        } else {
            setChangeGiven(0);
        }
    }, [amountPaid, total, paymentMethod]);

    // Fetch cash register status
    const fetchCashRegister = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/cash-register/current`);
            setCashRegister(response.data);
        } catch (error) {
            console.error('Error fetching cash register:', error);
            setCashRegister(null);
        } finally {
            setCashRegisterLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCombos();
        fetchCashRegister();
    }, []);

    const fetchCombos = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/combos`);
            setCombos(response.data);
        } catch (error) {
            console.error('Error fetching combos:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            // Fetch products, combos and composite products
            const [productsResponse, combosResponse, compositeResponse] = await Promise.all([
                axios.get(`${API_URL}/api/products`),
                axios.get(`${API_URL}/api/combos`),
                axios.get(`${API_URL}/api/composite-products`)
            ]);
            
            // Combine all products
            const allProducts = [
                ...productsResponse.data.map(p => ({ ...p, is_combo: false, is_composite: false })),
                ...combosResponse.data.map(c => ({ 
                    ...c, 
                    is_combo: true, 
                    is_composite: false,
                    category: 'Combos'
                })),
                ...compositeResponse.data.map(cp => ({ 
                    ...cp, 
                    is_combo: false, 
                    is_composite: true,
                    category: 'Dose Pronta'
                }))
            ];
            
            setProducts(allProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Erro ao carregar produtos');
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === 'Todos' || 
                               product.category === selectedCategory ||
                               (selectedCategory === 'Combos' && product.is_combo) ||
                               (selectedCategory === 'Dose Pronta' && product.is_composite);
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
        // Para produtos vendidos por dose, verificar estoque matriz (stock_quantity) e doses na garrafa aberta
        // Para combos e produtos compostos, sempre tem estoque virtual (calculado)
        const hasStock = product.is_combo || product.is_composite
            ? (product.stock_quantity > 0 || product.active)
            : product.sold_by_dose 
                ? (product.stock_quantity > 0 || product.open_bottle_doses_remaining > 0)
                : product.stock_quantity > 0;
        const isActive = product.active !== false;
        return matchesCategory && matchesSearch && hasStock && isActive;
    });

    // Filter combos - Deprecated, now combos are included in products
    const filteredCombos = [];

    const addToCart = (product) => {
        // Se o produto é vendido por dose, abrir dialog para escolher tipo
        if (product.sold_by_dose && !product.is_combo && !product.is_composite) {
            setDoseProduct(product);
            setSelectedSaleType('dose');
            setDoseDialogOpen(true);
            return;
        }
        
        // Se é produto composto com grupos de opções (ex: Gin de 10)
        if (product.is_composite && product.ingredient_groups && product.ingredient_groups.length > 0) {
            setCompositeOptionProduct(product);
            // Inicializar seleções vazias para cada grupo
            const initialSelections = {};
            product.ingredient_groups.forEach(group => {
                initialSelections[group.group_name] = null;
            });
            setCompositeSelections(initialSelections);
            setCompositeOptionDialogOpen(true);
            return;
        }
        
        // Determinar preço atual (promocional ou normal)
        const currentPrice = product.on_promotion && product.promotional_price 
            ? product.promotional_price 
            : product.price;
        
        // Determinar ID do item (combo, composite ou produto normal)
        let itemId;
        if (product.is_combo) {
            itemId = `combo_${product.id}`;
        } else if (product.is_composite) {
            itemId = `composite_${product.id}`;
        } else {
            itemId = product.id;
        }
        
        const existingItem = cart.find(item => item.product_id === itemId);
        
        if (existingItem) {
            if (existingItem.quantity >= product.stock_quantity) {
                toast.error('Estoque insuficiente');
                return;
            }
            setCart(cart.map(item => 
                item.product_id === itemId
                    ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
                    : item
            ));
        } else {
            setCart([...cart, {
                product_id: itemId,
                product_name: product.name,
                quantity: 1,
                unit_price: currentPrice,
                total_price: currentPrice,
                max_stock: product.stock_quantity,
                is_combo: product.is_combo || false,
                combo_id: product.is_combo ? product.id : null,
                combo_items: product.is_combo ? product.items : null,
                is_composite: product.is_composite || false,
                composite_id: product.is_composite ? product.id : null,
                composite_ingredients: product.is_composite ? product.ingredients : null,
                on_promotion: product.on_promotion || false,
                original_price: product.price
            }]);
        }
        toast.success(`${product.name} adicionado`);
    };

    const addDoseProductToCart = () => {
        if (!doseProduct) return;

        const saleType = selectedSaleType;
        const cartKey = `${doseProduct.id}_${saleType}`; // Unique key for dose vs bottle
        
        // Determinar preço considerando promoção
        // promotional_price é o preço da GARRAFA em promoção
        let price;
        if (doseProduct.on_promotion && doseProduct.promotional_price) {
            if (saleType === 'dose') {
                // Preço da dose = preço da garrafa promocional / doses por garrafa
                price = doseProduct.promotional_price / (doseProduct.doses_per_bottle || 1);
            } else {
                // Preço da garrafa = preço promocional direto
                price = doseProduct.promotional_price;
            }
        } else {
            // Sem promoção, usar preços normais
            price = saleType === 'dose' ? doseProduct.dose_price : doseProduct.price;
        }
        
        // Calcular estoque disponível - stock_quantity é o estoque matriz de garrafas
        let maxStock;
        if (saleType === 'dose') {
            // Total de doses disponíveis: garrafas (stock_quantity) * doses por garrafa + doses na garrafa aberta
            maxStock = (doseProduct.stock_quantity * doseProduct.doses_per_bottle) + doseProduct.open_bottle_doses_remaining;
        } else {
            // Apenas garrafas do estoque matriz
            maxStock = doseProduct.stock_quantity;
        }
        
        const existingItem = cart.find(item => 
            item.product_id === doseProduct.id && 
            item.sale_type === saleType && 
            !item.is_combo
        );
        
        // Nome do item no formato "Dose – Nome do Produto" ou "Garrafa – Nome do Produto"
        const itemName = saleType === 'dose' 
            ? `Dose – ${doseProduct.name}`
            : `Garrafa – ${doseProduct.name}`;
        
        if (existingItem) {
            if (existingItem.quantity >= maxStock) {
                toast.error('Estoque insuficiente');
                return;
            }
            setCart(cart.map(item => 
                item.product_id === doseProduct.id && item.sale_type === saleType && !item.is_combo
                    ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
                    : item
            ));
        } else {
            setCart([...cart, {
                product_id: doseProduct.id,
                product_name: itemName,
                quantity: 1,
                unit_price: price,
                total_price: price,
                max_stock: maxStock,
                is_combo: false,
                sale_type: saleType,
                original_product_id: doseProduct.id,  // Referência ao produto pai
                on_promotion: doseProduct.on_promotion || false
            }]);
        }
        
        toast.success(`${itemName} adicionado`);
        setDoseDialogOpen(false);
        setDoseProduct(null);
    };

    const addComboToCart = (combo) => {
        const existingItem = cart.find(item => item.combo_id === combo.id && item.is_combo);
        
        if (existingItem) {
            setCart(cart.map(item => 
                item.combo_id === combo.id && item.is_combo
                    ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
                    : item
            ));
        } else {
            setCart([...cart, {
                product_id: `combo_${combo.id}`,
                product_name: combo.name,
                quantity: 1,
                unit_price: combo.price,
                total_price: combo.price,
                is_combo: true,
                combo_id: combo.id,
                combo_items: combo.items
            }]);
        }
        toast.success(`${combo.name} adicionado`);
    };

    // Função para adicionar produto composto com seleções de opções ao carrinho
    const addCompositeWithSelectionsToCart = () => {
        if (!compositeOptionProduct) return;
        
        // Verificar se todas as opções obrigatórias foram selecionadas
        const missingGroups = compositeOptionProduct.ingredient_groups
            .filter(g => g.required !== false && !compositeSelections[g.group_name])
            .map(g => g.group_name);
        
        if (missingGroups.length > 0) {
            toast.error(`Selecione: ${missingGroups.join(', ')}`);
            return;
        }
        
        // Criar array de ingredientes selecionados
        const selectedIngredients = compositeOptionProduct.ingredient_groups
            .filter(g => compositeSelections[g.group_name])
            .map(g => ({
                group_name: g.group_name,
                product_id: compositeSelections[g.group_name].product_id,
                product_name: compositeSelections[g.group_name].product_name,
                quantity: g.quantity || 1,
                use_dose: g.use_dose
            }));
        
        // Criar nome descritivo do item (ex: "Gin de 10 (Maçã Verde + Melancia + Coco)")
        const selectionsText = selectedIngredients.map(s => {
            // Remover prefixos comuns para deixar nome mais curto
            return s.product_name
                .replace(/Eternity\s*/gi, '')
                .replace(/Eternit\s*/gi, '')
                .replace(/Baly\s*/gi, '')
                .replace(/Gelo\s*/gi, '')
                .trim();
        }).join(' + ');
        const itemName = `${compositeOptionProduct.name} (${selectionsText})`;
        
        // Criar ID único baseado nas seleções
        const selectionsKey = selectedIngredients.map(s => s.product_id).sort().join('_');
        const itemId = `composite_${compositeOptionProduct.id}_${selectionsKey}`;
        
        const existingItem = cart.find(item => item.product_id === itemId);
        
        if (existingItem) {
            setCart(cart.map(item => 
                item.product_id === itemId
                    ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
                    : item
            ));
        } else {
            setCart([...cart, {
                product_id: itemId,
                product_name: itemName,
                quantity: 1,
                unit_price: compositeOptionProduct.price,
                total_price: compositeOptionProduct.price,
                max_stock: 99, // Dinâmico baseado no estoque dos ingredientes
                is_combo: false,
                is_composite: true,
                composite_id: compositeOptionProduct.id,
                selected_ingredients: selectedIngredients // Ingredientes selecionados para baixa no estoque
            }]);
        }
        
        toast.success(`${itemName} adicionado!`);
        setCompositeOptionDialogOpen(false);
        setCompositeOptionProduct(null);
        setCompositeSelections({});
    };

    const updateQuantity = (productId, delta, saleType = null) => {
        setCart(cart.map(item => {
            // Para produtos com sale_type, verificar se é o mesmo tipo
            const isSameItem = saleType 
                ? (item.product_id === productId && item.sale_type === saleType)
                : (item.product_id === productId && !item.sale_type);
            
            if (isSameItem) {
                const newQuantity = item.quantity + delta;
                if (newQuantity <= 0) return null;
                // Skip stock check for combos (backend will validate)
                if (!item.is_combo && newQuantity > item.max_stock) {
                    toast.error('Estoque insuficiente');
                    return item;
                }
                return { ...item, quantity: newQuantity, total_price: newQuantity * item.unit_price };
            }
            return item;
        }).filter(Boolean));
    };

    const removeFromCart = (productId, saleType = null) => {
        setCart(cart.filter(item => {
            if (saleType) {
                return !(item.product_id === productId && item.sale_type === saleType);
            }
            return item.product_id !== productId || item.sale_type;
        }));
    };

    const clearCart = () => {
        setCart([]);
        setDiscount(0);
    };

    const handleCheckout = async () => {
        if (cart.length === 0) {
            toast.error('Carrinho vazio');
            return;
        }

        // Validação para pagamento único em dinheiro
        if (!splitPaymentMode && paymentMethod === 'dinheiro') {
            const paidAmount = parseFloat(amountPaid) || 0;
            if (!amountPaid || paidAmount <= 0) {
                toast.error('Informe o valor recebido para pagamento em dinheiro');
                return;
            }
            if (paidAmount < total) {
                toast.error(`Valor recebido (R$${paidAmount.toFixed(2)}) é menor que o total (R$${total.toFixed(2)})`);
                return;
            }
        }
        
        // Validação para pagamento dividido
        if (splitPaymentMode) {
            if (payments.length === 0) {
                toast.error('Adicione pelo menos um pagamento');
                return;
            }
            const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
            if (totalPayments < total) {
                toast.error(`Falta pagar: ${formatCurrency(total - totalPayments)}`);
                return;
            }
        }

        setProcessing(true);

        try {
            const saleData = {
                items: cart.map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    total_price: item.total_price,
                    is_combo: item.is_combo || false,
                    combo_id: item.combo_id || null,
                    combo_items: item.combo_items || null,
                    sale_type: item.sale_type || null,
                    is_composite: item.is_composite || false,
                    composite_id: item.composite_id || null,
                    selected_ingredients: item.selected_ingredients || null
                })),
                payment_method: splitPaymentMode ? 'dividido' : paymentMethod,
                customer_name: customerName || null,
                customer_phone: customerPhone || null,
                delivery_address: null,
                is_delivery: false,
                discount: discount,
                sale_origin: saleOrigin,
                // Campos para pagamento único em dinheiro
                amount_paid: !splitPaymentMode && paymentMethod === 'dinheiro' && amountPaid ? parseFloat(amountPaid) : null,
                change_given: !splitPaymentMode && paymentMethod === 'dinheiro' && changeGiven > 0 ? changeGiven : null,
                // Pagamentos divididos
                payments: splitPaymentMode ? payments : null
            };

            const response = await axios.post(`${API_URL}/api/sales`, saleData);
            
            toast.success('Venda realizada com sucesso!');
            clearCart();
            setCheckoutOpen(false);
            resetCheckoutForm();
            fetchProducts();
        } catch (error) {
            console.error('Error creating sale:', error);
            const errorMsg = error.response?.data?.detail || 'Erro ao finalizar venda';
            if (errorMsg.includes('caixa') || errorMsg.includes('Caixa')) {
                toast.error(
                    <div className="space-y-1">
                        <div className="font-bold text-orange-500 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            CAIXA FECHADO!
                        </div>
                        <div className="text-sm">{errorMsg}</div>
                    </div>,
                    { 
                        duration: 8000,
                        style: { 
                            background: '#1a1005', 
                            border: '2px solid #f97316',
                            color: '#fed7aa'
                        }
                    }
                );
            } else if (errorMsg.includes('Sem estoque') || errorMsg.includes('estoque') || errorMsg.includes('SEM ESTOQUE')) {
                toast.error(
                    <div className="space-y-1">
                        <div className="font-bold text-red-500 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            ⚠️ SEM ESTOQUE!
                        </div>
                        <div className="text-sm">{errorMsg}</div>
                    </div>,
                    { 
                        duration: 10000,
                        style: { 
                            background: '#1a0505', 
                            border: '2px solid #ef4444',
                            color: '#fecaca'
                        }
                    }
                );
                fetchProducts();
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setProcessing(false);
        }
    };
    
    // Funções para pagamento dividido
    const addPayment = () => {
        const amount = parseFloat(currentPaymentAmount) || 0;
        if (amount <= 0) {
            toast.error('Informe um valor válido');
            return;
        }
        if (amount > remaining + 0.01) { // Pequena tolerância para arredondamentos
            toast.error(`Valor maior que o restante (${formatCurrency(remaining)})`);
            return;
        }
        
        let newPayment = {
            method: currentPaymentMethod,
            amount: amount
        };
        
        // Para pagamento em dinheiro no modo dividido, apenas registrar o valor
        // (sem necessidade de informar valor recebido/troco)
        if (currentPaymentMethod === 'dinheiro') {
            newPayment.amount_paid = amount;
            newPayment.change_given = 0;
        }
        
        setPayments([...payments, newPayment]);
        setCurrentPaymentAmount('');
        
        // Se cobriu o total, mostrar mensagem
        const newTotalPaid = payments.reduce((sum, p) => sum + p.amount, 0) + amount;
        if (newTotalPaid >= total) {
            toast.success('Total coberto! Finalize a venda.');
        }
    };
    
    const removePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };
    
    const getPaymentMethodLabel = (method) => {
        const labels = {
            'dinheiro': 'Dinheiro',
            'credito': 'Crédito',
            'debito': 'Débito',
            'pix': 'PIX'
        };
        return labels[method] || method;
    };

    const resetCheckoutForm = () => {
        setPaymentMethod('dinheiro');
        setSaleOrigin('loja_fisica');
        setCustomerName('');
        setCustomerPhone('');
        setDiscount(0);
        setAmountPaid('');
        setChangeGiven(0);
        // Reset pagamento dividido
        setSplitPaymentMode(false);
        setPayments([]);
        setCurrentPaymentMethod('dinheiro');
        setCurrentPaymentAmount('');
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    if (loading || cashRegisterLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Check if cash register is closed
    if (!cashRegister) {
        return (
            <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
                <Card className="w-full max-w-md bg-[#1a1a1a] border-red-500/50">
                    <CardHeader className="text-center pb-2">
                        <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>
                        <CardTitle className="text-xl text-white">Caixa Fechado!</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center space-y-4">
                        <p className="text-gray-400">
                            O caixa precisa estar aberto para realizar vendas no PDV.
                        </p>
                        <p className="text-gray-500 text-sm">
                            Abra o caixa com o fundo de troco inicial para começar a vender.
                        </p>
                        <Button 
                            className="w-full bg-red-500 text-white hover:bg-red-400 font-semibold"
                            onClick={() => window.location.href = '/admin/caixa'}
                        >
                            <Banknote className="w-4 h-4 mr-2" />
                            Abrir Caixa
                        </Button>
                        <Button 
                            variant="outline"
                            className="w-full border-[#262626] text-gray-400 hover:text-white"
                            onClick={fetchCashRegister}
                        >
                            Verificar Novamente
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div className="min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex flex-col lg:flex-row gap-4 lg:gap-6" data-testid="pdv-page">
            {/* Scanner Indicator */}
            {isScanning && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                    <ScanBarcode className="w-5 h-5" />
                    <span className="font-medium">Lendo código de barras...</span>
                </div>
            )}

            {/* Products Section */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="mb-4 lg:mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">PDV</h1>
                        <p className="text-gray-400 text-sm lg:text-base mt-1">
                            Ponto de Venda
                            <span className="ml-2 inline-flex items-center gap-1 text-red-400">
                                <ScanBarcode className="w-4 h-4" />
                                Scanner ativo
                            </span>
                        </p>
                    </div>
                    {/* Mobile Cart Button */}
                    <Button
                        className="lg:hidden bg-red-500 text-white hover:bg-red-400 relative"
                        onClick={() => setMobileCartOpen(true)}
                    >
                        <ShoppingCart className="w-5 h-5" />
                        {cartCount > 0 && (
                            <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {cartCount}
                            </span>
                        )}
                    </Button>
                </div>

                {/* Search and Categories */}
                <div className="space-y-3 lg:space-y-4 mb-4 lg:mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                        <Input
                            placeholder="Buscar produto..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 lg:pl-11 bg-[#121212] border-[#262626] text-white h-10 lg:h-12 text-sm lg:text-base"
                            data-testid="search-input"
                        />
                    </div>
                    
                    {/* Category Filter - Horizontal Scroll */}
                    <div className="overflow-x-auto pb-2 -mx-2 px-2">
                        <div className="flex gap-2 min-w-max">
                            {CATEGORIES.map(category => (
                                <Button
                                    key={category}
                                    size="sm"
                                    variant={selectedCategory === category ? "default" : "outline"}
                                    className={`text-xs lg:text-sm whitespace-nowrap ${selectedCategory === category 
                                        ? "bg-red-500 text-white hover:bg-red-400" 
                                        : "border-[#262626] text-gray-400 hover:text-white hover:border-red-500"
                                    }`}
                                    onClick={() => setSelectedCategory(category)}
                                    data-testid={`category-${category.toLowerCase()}`}
                                >
                                    {category}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Products Grid */}
                <ScrollArea className="flex-1">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2 lg:gap-4 pb-4">
                        {/* Combos first */}
                        {filteredCombos.map(combo => (
                            <Card 
                                key={`combo-${combo.id}`}
                                className="bg-purple-500/5 border-purple-500/30 hover:border-purple-500/50 transition-all cursor-pointer active:scale-95 lg:active:scale-100"
                                onClick={() => addComboToCart(combo)}
                                data-testid={`combo-card-${combo.id}`}
                            >
                                <CardContent className="p-2 lg:p-4">
                                    <div className="aspect-square bg-purple-500/10 rounded-md mb-2 lg:mb-3 flex items-center justify-center relative">
                                        <Gift className="w-8 h-8 lg:w-12 lg:h-12 text-purple-500/50" />
                                        <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-purple-500 text-white text-[10px] lg:text-xs px-1.5 lg:px-2">
                                            COMBO
                                        </Badge>
                                    </div>
                                    <h3 className="font-medium text-white text-xs lg:text-sm truncate">{combo.name}</h3>
                                    <div className="text-[10px] lg:text-xs text-gray-500 mt-1 truncate hidden sm:block">
                                        {combo.items.map(i => `${i.quantity}x ${i.product_name.split(' ')[0]}`).join(' + ')}
                                    </div>
                                    <div className="flex items-center justify-between mt-1 lg:mt-2">
                                        <p className="text-purple-400 font-bold text-sm lg:text-base">{formatCurrency(combo.price)}</p>
                                        <span className="text-[10px] lg:text-xs text-emerald-500 hidden sm:inline">
                                            +{formatCurrency(combo.profit_margin)}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                        {/* Products */}
                        {filteredProducts.map(product => (
                            <Card 
                                key={product.id}
                                className={`bg-[#121212] border-[#262626] hover:border-red-500/50 transition-all cursor-pointer active:scale-95 lg:active:scale-100 ${product.sold_by_dose ? 'border-purple-500/30' : ''} ${product.on_promotion ? 'ring-2 ring-red-500/50' : ''}`}
                                onClick={() => addToCart(product)}
                                data-testid={`product-card-${product.id}`}
                            >
                                <CardContent className="p-2 lg:p-4">
                                    <div className="aspect-square bg-[#0a0a0a] rounded-md mb-2 lg:mb-3 flex items-center justify-center relative">
                                        {product.image_url ? (
                                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover rounded-md" />
                                        ) : (
                                            <span className="text-2xl lg:text-4xl text-red-500/30">
                                                {product.is_combo ? '🎁' :
                                                 product.is_composite ? '🍹' :
                                                 product.category === 'Cerveja' ? '🍺' : 
                                                 product.category === 'Refrigerante' ? '🥤' :
                                                 product.category === 'Vinhos' ? '🍷' :
                                                 product.category === 'Destilados' ? '🥃' :
                                                 product.category === 'Energéticos' ? '⚡' :
                                                 product.category === 'Gelo' ? '🧊' :
                                                 product.category === 'Tabacaria e Cigarro' ? '🚬' : '📦'}
                                            </span>
                                        )}
                                        {/* Promo badge */}
                                        {product.on_promotion && (
                                            <Badge className="absolute top-1 left-1 lg:top-2 lg:left-2 bg-red-500 text-white text-[10px] lg:text-xs px-1.5 lg:px-2 animate-pulse">
                                                🔥 PROMO
                                            </Badge>
                                        )}
                                        {/* Combo badge */}
                                        {product.is_combo && (
                                            <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-red-500 text-white text-[10px] lg:text-xs px-1.5">
                                                COMBO
                                            </Badge>
                                        )}
                                        {/* Composite product badge */}
                                        {product.is_composite && (
                                            <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-orange-500 text-white text-[10px] lg:text-xs px-1.5">
                                                🍹 DRINK
                                            </Badge>
                                        )}
                                        {/* Discount percentage */}
                                        {product.on_promotion && product.discount_percentage > 0 && !product.is_combo && !product.is_composite && (
                                            <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-red-600 text-white text-[10px] lg:text-xs px-1.5 font-bold">
                                                -{Math.round(product.discount_percentage)}%
                                            </Badge>
                                        )}
                                    </div>
                                    <h3 className="font-medium text-white text-xs lg:text-sm truncate">{product.name}</h3>
                                    {/* Composite product ingredients */}
                                    {product.is_composite && product.ingredients && (
                                        <p className="text-[10px] text-gray-500 truncate">
                                            {product.ingredients.map(i => `${i.quantity}x ${i.product_name.split(' ')[0]}`).join(' + ')}
                                        </p>
                                    )}
                                    {/* Combo items */}
                                    {product.is_combo && product.items && (
                                        <p className="text-[10px] text-gray-500 truncate">
                                            {product.items.map(i => `${i.quantity}x ${i.product_name.split(' ')[0]}`).join(' + ')}
                                        </p>
                                    )}
                                    {product.sold_by_dose && !product.is_composite ? (
                                        <div className="mt-1 lg:mt-2">
                                            {product.on_promotion && product.promotional_price ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center justify-between text-[10px] lg:text-xs">
                                                        <span className="text-gray-400">Dose:</span>
                                                        <span className="text-gray-400">Garrafa:</span>
                                                    </div>
                                                    <div className="flex items-center justify-between text-[10px] lg:text-xs">
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-gray-500 line-through text-[9px]">{formatCurrency(product.dose_price)}</span>
                                                            <span className="text-red-500 font-bold">{formatCurrency(product.promotional_price / (product.doses_per_bottle || 1))}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-gray-500 line-through text-[9px]">{formatCurrency(product.price)}</span>
                                                            <span className="text-red-500 font-bold">{formatCurrency(product.promotional_price)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-[10px] lg:text-xs text-purple-300">
                                                        {product.stock_quantity}gf / {product.open_bottle_doses_remaining}ds
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center justify-between text-[10px] lg:text-xs text-gray-500">
                                                        <span>Dose: <span className="text-purple-400 font-bold">{formatCurrency(product.dose_price)}</span></span>
                                                        <span>Gf: <span className="text-red-500 font-bold">{formatCurrency(product.price)}</span></span>
                                                    </div>
                                                    <div className="text-[10px] lg:text-xs text-purple-300 mt-1">
                                                        {product.stock_quantity}gf / {product.open_bottle_doses_remaining}ds
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : product.on_promotion ? (
                                        <div className="mt-1 lg:mt-2">
                                            <div className="flex items-center gap-2">
                                                <p className="text-gray-500 line-through text-xs">{formatCurrency(product.price)}</p>
                                                <p className="text-red-500 font-bold text-sm lg:text-base">{formatCurrency(product.promotional_price)}</p>
                                            </div>
                                            <Badge variant="outline" className="border-[#333] text-gray-400 text-[10px] lg:text-xs px-1.5 lg:px-2 mt-1">
                                                Est: {product.stock_quantity}
                                            </Badge>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between mt-1 lg:mt-2">
                                            <p className="text-red-500 font-bold text-sm lg:text-base">{formatCurrency(product.price)}</p>
                                            <Badge variant="outline" className="border-[#333] text-gray-400 text-[10px] lg:text-xs px-1.5 lg:px-2">
                                                {product.stock_quantity}
                                            </Badge>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                        {filteredProducts.length === 0 && filteredCombos.length === 0 && (
                            <div className="col-span-full text-center py-8 lg:py-12 text-gray-500 text-sm lg:text-base">
                                Nenhum produto encontrado
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </div>

            {/* Desktop Cart Section */}
            <Card className="hidden lg:flex w-96 bg-[#0a0a0a] border-[#1a1a1a] flex-col" data-testid="cart-section">
                <CardHeader className="border-b border-[#1a1a1a]">
                    <CardTitle className="text-white font-heading flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-red-500" />
                        Carrinho
                        {cart.length > 0 && (
                            <Badge className="bg-red-500 text-white ml-auto">{cart.length}</Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                
                <ScrollArea className="flex-1">
                    <CardContent className="p-4 space-y-3">
                        {cart.map(item => (
                            <div 
                                key={`${item.product_id}_${item.sale_type || 'normal'}`}
                                className={`flex items-center gap-3 p-3 rounded-md border ${
                                    item.is_combo 
                                        ? 'bg-purple-500/5 border-purple-500/30' 
                                        : item.sale_type
                                            ? 'bg-purple-500/5 border-purple-500/20'
                                            : 'bg-[#121212] border-[#1a1a1a]'
                                }`}
                                data-testid={`cart-item-${item.product_id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {item.is_combo && <Gift className="w-4 h-4 text-purple-500" />}
                                        {item.sale_type && <Wine className="w-4 h-4 text-purple-400" />}
                                        <p className="text-white text-sm font-medium truncate">{item.product_name}</p>
                                    </div>
                                    <p className={`text-sm ${item.is_combo || item.sale_type ? 'text-purple-400' : 'text-red-500'}`}>
                                        {formatCurrency(item.unit_price)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-gray-400 hover:text-white"
                                        onClick={() => updateQuantity(item.product_id, -1, item.sale_type)}
                                        data-testid={`decrease-${item.product_id}`}
                                    >
                                        <Minus className="w-4 h-4" />
                                    </Button>
                                    <span className="text-white w-8 text-center">{item.quantity}</span>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-gray-400 hover:text-white"
                                        onClick={() => updateQuantity(item.product_id, 1, item.sale_type)}
                                        data-testid={`increase-${item.product_id}`}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-red-500 hover:text-red-400"
                                    onClick={() => removeFromCart(item.product_id, item.sale_type)}
                                    data-testid={`remove-${item.product_id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                        {cart.length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                                Carrinho vazio
                            </div>
                        )}
                    </CardContent>
                </ScrollArea>

                {/* Cart Footer */}
                <div className="border-t border-[#1a1a1a] p-4 space-y-4">
                    <div className="space-y-2">
                        <div className="flex justify-between text-gray-400">
                            <span>Subtotal</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-emerald-500">
                                <span>Desconto</span>
                                <span>-{formatCurrency(discount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-xl font-bold text-white">
                            <span>Total</span>
                            <span className="text-red-500">{formatCurrency(total)}</span>
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="flex-1 border-[#262626] text-gray-400 hover:text-white"
                            onClick={clearCart}
                            disabled={cart.length === 0}
                            data-testid="clear-cart-btn"
                        >
                            Limpar
                        </Button>
                        <Button
                            className="flex-1 bg-red-500 text-white hover:bg-red-400 font-bold"
                            onClick={() => setCheckoutOpen(true)}
                            disabled={cart.length === 0}
                            data-testid="checkout-btn"
                        >
                            Finalizar
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Mobile Cart Drawer */}
            <Sheet open={mobileCartOpen} onOpenChange={setMobileCartOpen}>
                <SheetContent side="right" className="w-full sm:w-96 p-0 bg-[#0a0a0a] border-l border-[#1a1a1a]">
                    <SheetHeader className="p-4 border-b border-[#1a1a1a]">
                        <SheetTitle className="text-white font-heading flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-red-500" />
                            Carrinho
                            {cart.length > 0 && (
                                <Badge className="bg-red-500 text-white ml-auto">{cart.length}</Badge>
                            )}
                        </SheetTitle>
                    </SheetHeader>
                    
                    <ScrollArea className="flex-1 h-[calc(100vh-280px)]">
                        <div className="p-4 space-y-3">
                            {cart.map(item => (
                                <div 
                                    key={item.product_id}
                                    className={`flex items-center gap-2 p-2 rounded-md border ${
                                        item.is_combo 
                                            ? 'bg-purple-500/5 border-purple-500/30' 
                                            : 'bg-[#121212] border-[#1a1a1a]'
                                    }`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                            {item.is_combo && <Gift className="w-3 h-3 text-purple-500" />}
                                            <p className="text-white text-xs font-medium truncate">{item.product_name}</p>
                                        </div>
                                        <p className={`text-xs ${item.is_combo ? 'text-purple-400' : 'text-red-500'}`}>
                                            {formatCurrency(item.unit_price)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button size="icon" variant="ghost" className="h-7 w-7 text-gray-400" onClick={() => updateQuantity(item.product_id, -1)}>
                                            <Minus className="w-3 h-3" />
                                        </Button>
                                        <span className="text-white w-5 text-center text-sm">{item.quantity}</span>
                                        <Button size="icon" variant="ghost" className="h-7 w-7 text-gray-400" onClick={() => updateQuantity(item.product_id, 1)}>
                                            <Plus className="w-3 h-3" />
                                        </Button>
                                    </div>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeFromCart(item.product_id)}>
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            ))}
                            {cart.length === 0 && (
                                <div className="text-center py-8 text-gray-500 text-sm">
                                    Carrinho vazio
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    {/* Mobile Cart Footer */}
                    <div className="absolute bottom-0 left-0 right-0 border-t border-[#1a1a1a] p-4 space-y-3 bg-[#0a0a0a]">
                        <div className="space-y-1">
                            <div className="flex justify-between text-gray-400 text-sm">
                                <span>Subtotal</span>
                                <span>{formatCurrency(subtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-emerald-500 text-sm">
                                    <span>Desconto</span>
                                    <span>-{formatCurrency(discount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-lg font-bold text-white">
                                <span>Total</span>
                                <span className="text-red-500">{formatCurrency(total)}</span>
                            </div>
                        </div>
                        
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 border-[#262626] text-gray-400"
                                onClick={clearCart}
                                disabled={cart.length === 0}
                            >
                                Limpar
                            </Button>
                            <Button
                                size="sm"
                                className="flex-1 bg-red-500 text-white hover:bg-red-400 font-bold"
                                onClick={() => { setMobileCartOpen(false); setCheckoutOpen(true); }}
                                disabled={cart.length === 0}
                            >
                                Finalizar
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Checkout Dialog */}
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white w-[calc(100%-2rem)] sm:w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl font-heading">Finalizar Venda</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
                        {/* Toggle Pagamento Dividido */}
                        <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-[#262626]">
                            <div>
                                <p className="text-white font-medium">Pagamento Dividido</p>
                                <p className="text-gray-500 text-xs">Dividir em múltiplas formas de pagamento</p>
                            </div>
                            <Button
                                variant={splitPaymentMode ? "default" : "outline"}
                                size="sm"
                                className={splitPaymentMode 
                                    ? "bg-red-500 text-white hover:bg-red-400" 
                                    : "border-[#262626] text-gray-400"
                                }
                                onClick={() => {
                                    setSplitPaymentMode(!splitPaymentMode);
                                    setPayments([]);
                                }}
                            >
                                {splitPaymentMode ? 'Ativo' : 'Inativo'}
                            </Button>
                        </div>

                        {/* Modo Pagamento Único */}
                        {!splitPaymentMode && (
                            <>
                                {/* Payment Method */}
                                <div className="space-y-2 sm:space-y-3">
                                    <Label className="text-gray-400 text-xs uppercase tracking-wider">Forma de Pagamento</Label>
                                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                        {[
                                            { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
                                            { value: 'cartao', label: 'Cartão', icon: CreditCard },
                                            { value: 'pix', label: 'PIX', icon: QrCode },
                                        ].map(method => (
                                            <Button
                                                key={method.value}
                                                size="sm"
                                                variant={paymentMethod === method.value ? "default" : "outline"}
                                                className={`text-xs sm:text-sm ${paymentMethod === method.value 
                                                    ? "bg-red-500 text-white" 
                                                    : "border-[#262626] text-gray-400"
                                                }`}
                                                onClick={() => setPaymentMethod(method.value)}
                                                data-testid={`payment-${method.value}`}
                                            >
                                                <method.icon className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                                                <span className="hidden sm:inline">{method.label}</span>
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                {/* Cash Payment - Amount Paid and Change */}
                                {paymentMethod === 'dinheiro' && (
                                    <div className="p-4 bg-red-500/5 rounded-md border border-red-500/30 space-y-4">
                                        <h4 className="text-sm font-bold text-red-400 uppercase tracking-wider">Pagamento em Dinheiro</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-gray-400">Valor Recebido</Label>
                                                <Input
                                                    type="number"
                                                    min={total}
                                                    step="0.01"
                                                    value={amountPaid}
                                                    onChange={(e) => setAmountPaid(e.target.value)}
                                                    className="bg-[#121212] border-[#262626] text-white"
                                                    placeholder={formatCurrency(total)}
                                                    data-testid="amount-paid-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-gray-400">Troco</Label>
                                                <div className="h-10 px-3 bg-[#0a0a0a] border border-[#262626] rounded-md flex items-center">
                                                    <span className={`font-bold ${changeGiven > 0 ? 'text-amber-500' : 'text-gray-500'}`}>
                                                        {formatCurrency(changeGiven)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Modo Pagamento Dividido */}
                        {splitPaymentMode && (
                            <div className="space-y-4">
                                {/* Progresso do Pagamento */}
                                <div className="p-4 bg-[#121212] rounded-lg border border-[#262626]">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-400">Total da Venda</span>
                                        <span className="text-white font-bold">{formatCurrency(total)}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-400">Já Pago</span>
                                        <span className="text-green-500 font-bold">{formatCurrency(totalPaid)}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-[#262626]">
                                        <span className="text-gray-400">Falta Pagar</span>
                                        <span className={`font-bold ${remaining > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                            {formatCurrency(remaining)}
                                        </span>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="mt-3 h-2 bg-[#262626] rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-gradient-to-r from-red-500 to-green-500 transition-all duration-300"
                                            style={{ width: `${Math.min(100, (totalPaid / total) * 100)}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Lista de Pagamentos Adicionados */}
                                {payments.length > 0 && (
                                    <div className="space-y-2">
                                        <Label className="text-gray-400 text-xs uppercase tracking-wider">Pagamentos Adicionados</Label>
                                        <div className="space-y-2">
                                            {payments.map((payment, index) => (
                                                <div key={index} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#262626]">
                                                    <div className="flex items-center gap-3">
                                                        {payment.method === 'dinheiro' && <Banknote className="w-4 h-4 text-green-500" />}
                                                        {payment.method === 'credito' && <CreditCard className="w-4 h-4 text-blue-500" />}
                                                        {payment.method === 'debito' && <CreditCard className="w-4 h-4 text-purple-500" />}
                                                        {payment.method === 'pix' && <QrCode className="w-4 h-4 text-cyan-500" />}
                                                        <p className="text-white font-medium">{getPaymentMethodLabel(payment.method)}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white font-bold">{formatCurrency(payment.amount)}</span>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-500/20"
                                                            onClick={() => removePayment(index)}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Adicionar Novo Pagamento */}
                                {remaining > 0 && (
                                    <div className="p-4 bg-[#121212] rounded-lg border border-[#262626] space-y-4">
                                        <h4 className="text-sm font-bold text-gray-300 uppercase tracking-wider">Adicionar Pagamento</h4>
                                        
                                        {/* Método */}
                                        <div className="grid grid-cols-4 gap-2">
                                            {[
                                                { value: 'dinheiro', label: 'Dinheiro', icon: Banknote, color: 'green' },
                                                { value: 'credito', label: 'Crédito', icon: CreditCard, color: 'blue' },
                                                { value: 'debito', label: 'Débito', icon: CreditCard, color: 'purple' },
                                                { value: 'pix', label: 'PIX', icon: QrCode, color: 'cyan' },
                                            ].map(method => (
                                                <Button
                                                    key={method.value}
                                                    size="sm"
                                                    variant={currentPaymentMethod === method.value ? "default" : "outline"}
                                                    className={`text-xs flex-col h-auto py-2 ${currentPaymentMethod === method.value 
                                                        ? `bg-${method.color}-600 text-white` 
                                                        : "border-[#262626] text-gray-400"
                                                    }`}
                                                    onClick={() => setCurrentPaymentMethod(method.value)}
                                                >
                                                    <method.icon className="w-4 h-4 mb-1" />
                                                    <span className="text-[10px]">{method.label}</span>
                                                </Button>
                                            ))}
                                        </div>

                                        {/* Valor */}
                                        <div className="space-y-2">
                                            <Label className="text-gray-400 text-xs">Valor</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    min="0.01"
                                                    max={remaining}
                                                    step="0.01"
                                                    value={currentPaymentAmount}
                                                    onChange={(e) => setCurrentPaymentAmount(e.target.value)}
                                                    className="bg-[#1a1a1a] border-[#262626] text-white"
                                                    placeholder={`Máx: ${formatCurrency(remaining)}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Botão Adicionar */}
                                        <Button
                                            onClick={addPayment}
                                            disabled={!currentPaymentAmount || parseFloat(currentPaymentAmount) <= 0}
                                            className="w-full bg-red-500 text-white hover:bg-red-400"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Adicionar Pagamento
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Discount */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Desconto (R$)</Label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={discount}
                                onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="0.00"
                                data-testid="discount-input"
                            />
                        </div>

                        {/* Summary */}
                        <div className="p-4 bg-[#121212] rounded-md space-y-2">
                            <div className="flex justify-between text-gray-400">
                                <span>Subtotal</span>
                                <span>{formatCurrency(subtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-emerald-500">
                                    <span>Desconto</span>
                                    <span>-{formatCurrency(discount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-xl font-bold pt-2 border-t border-[#262626]">
                                <span className="text-white">Total</span>
                                <span className="text-red-500">{formatCurrency(total)}</span>
                            </div>
                            {!splitPaymentMode && paymentMethod === 'dinheiro' && changeGiven > 0 && (
                                <div className="flex justify-between text-amber-500 pt-2 border-t border-[#262626]">
                                    <span>Troco a devolver</span>
                                    <span className="font-bold">{formatCurrency(changeGiven)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCheckoutOpen(false)}
                            className="border-[#262626] text-gray-400"
                            data-testid="cancel-checkout-btn"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCheckout}
                            disabled={processing || (splitPaymentMode && totalPaid < total)}
                            className="bg-red-500 text-white hover:bg-red-400 font-bold"
                            data-testid="confirm-checkout-btn"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Processando...
                                </>
                            ) : (
                                `Confirmar ${formatCurrency(total)}`
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dose/Garrafa Selection Dialog */}
            <Dialog open={doseDialogOpen} onOpenChange={setDoseDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white w-[calc(100%-2rem)] sm:max-w-md" data-testid="dose-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl font-heading flex items-center gap-2">
                            <Wine className="w-5 h-5 text-purple-400" />
                            Escolher Tipo de Venda
                        </DialogTitle>
                    </DialogHeader>

                    {doseProduct && (
                        <div className="space-y-4 py-4">
                            <div className="text-center">
                                <h3 className="text-white font-medium text-lg">{doseProduct.name}</h3>
                                <p className="text-gray-500 text-sm mt-1">Como deseja vender?</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Opção Dose */}
                                <button
                                    onClick={() => setSelectedSaleType('dose')}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        selectedSaleType === 'dose'
                                            ? 'border-purple-500 bg-purple-500/10'
                                            : 'border-[#262626] hover:border-purple-500/50'
                                    }`}
                                >
                                    <Wine className="w-8 h-8 mx-auto text-purple-400 mb-2" />
                                    <p className="text-white font-medium">Dose</p>
                                    <p className="text-purple-400 font-bold text-lg mt-1">
                                        {formatCurrency(doseProduct.dose_price)}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-2">
                                        Disponível: {(doseProduct.stock_quantity * doseProduct.doses_per_bottle) + doseProduct.open_bottle_doses_remaining} doses
                                    </p>
                                </button>

                                {/* Opção Garrafa */}
                                <button
                                    onClick={() => setSelectedSaleType('garrafa')}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        selectedSaleType === 'garrafa'
                                            ? 'border-red-500 bg-red-500/10'
                                            : 'border-[#262626] hover:border-red-500/50'
                                    }`}
                                    disabled={doseProduct.stock_quantity === 0}
                                >
                                    <span className="text-3xl block mb-2">🍾</span>
                                    <p className="text-white font-medium">Garrafa</p>
                                    <p className="text-red-500 font-bold text-lg mt-1">
                                        {formatCurrency(doseProduct.price)}
                                    </p>
                                    <p className="text-gray-500 text-xs mt-2">
                                        Disponível: {doseProduct.stock_quantity} garrafas
                                    </p>
                                </button>
                            </div>

                            {/* Info sobre estoque */}
                            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 text-sm">
                                <p className="text-purple-300">
                                    <strong>Estoque atual:</strong> {doseProduct.stock_quantity} garrafas 
                                    {doseProduct.open_bottle_doses_remaining > 0 && (
                                        <> + {doseProduct.open_bottle_doses_remaining} doses na garrafa aberta</>
                                    )}
                                </p>
                                {doseProduct.doses_per_bottle && (
                                    <p className="text-gray-400 text-xs mt-1">
                                        Cada garrafa rende {doseProduct.doses_per_bottle} doses
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDoseDialogOpen(false);
                                setDoseProduct(null);
                            }}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={addDoseProductToCart}
                            className={selectedSaleType === 'dose' 
                                ? 'bg-purple-500 text-white hover:bg-purple-400 font-bold'
                                : 'bg-red-500 text-white hover:bg-red-400 font-bold'
                            }
                        >
                            Adicionar {selectedSaleType === 'dose' ? 'Dose' : 'Garrafa'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Composite Product Options Selection Dialog (ex: Gin de 10) */}
            <Dialog open={compositeOptionDialogOpen} onOpenChange={setCompositeOptionDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white w-[calc(100%-2rem)] sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="composite-options-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl font-heading flex items-center gap-2">
                            <span className="text-2xl">🍹</span>
                            Escolher Sabores
                        </DialogTitle>
                    </DialogHeader>

                    {compositeOptionProduct && (
                        <div className="space-y-4 py-2 overflow-y-auto flex-1">
                            <div className="text-center pb-3 border-b border-red-500/20">
                                <h3 className="text-red-500 font-bold text-xl">{compositeOptionProduct.name}</h3>
                                <p className="text-gray-400 text-sm mt-1">Escolha seus sabores favoritos</p>
                                <p className="text-red-500 font-bold text-2xl mt-2">{formatCurrency(compositeOptionProduct.price)}</p>
                            </div>

                            {/* Ingredient Groups */}
                            {compositeOptionProduct.ingredient_groups?.map((group) => (
                                <div key={group.group_name} className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">
                                            {group.group_name.toLowerCase().includes('gin') || group.group_name.toLowerCase().includes('eternity') ? '🥃' : 
                                             group.group_name.toLowerCase().includes('baly') || group.group_name.toLowerCase().includes('energetic') ? '⚡' : 
                                             group.group_name.toLowerCase().includes('gelo') ? '🧊' : '📦'}
                                        </span>
                                        <Label className="text-red-500 font-medium text-sm uppercase tracking-wide">
                                            {group.group_name} {group.required !== false && <span className="text-red-500">*</span>}
                                        </Label>
                                        {group.use_dose && <span className="text-xs text-blue-400">(dose)</span>}
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {group.options.map((option) => (
                                            <button
                                                key={option.product_id}
                                                onClick={() => setCompositeSelections(prev => ({
                                                    ...prev,
                                                    [group.group_name]: option
                                                }))}
                                                className={`p-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                                                    compositeSelections[group.group_name]?.product_id === option.product_id
                                                        ? 'border-red-500 bg-red-500/20 text-red-500'
                                                        : 'border-[#333] hover:border-red-500/50 text-gray-300'
                                                }`}
                                            >
                                                {option.product_name
                                                    .replace(/Eternity\s*/gi, '')
                                                    .replace(/Eternit\s*/gi, '')
                                                    .replace(/Baly\s*/gi, '')
                                                    .replace(/Gelo\s*/gi, '')
                                                    .trim()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* Summary of selections */}
                            {Object.keys(compositeSelections).some(k => compositeSelections[k]) && (
                                <div className="mt-4 p-3 bg-[#121212] rounded-lg border border-red-500/30">
                                    <p className="text-red-500 font-medium text-sm mb-2">Seu pedido:</p>
                                    <div className="space-y-1">
                                        {compositeOptionProduct.ingredient_groups?.map((group) => (
                                            <div key={group.group_name} className="flex justify-between text-xs">
                                                <span className="text-gray-400">{group.group_name}:</span>
                                                <span className={compositeSelections[group.group_name] ? 'text-white' : 'text-red-500'}>
                                                    {compositeSelections[group.group_name]?.product_name
                                                        ?.replace(/Eternity\s*/gi, '')
                                                        ?.replace(/Eternit\s*/gi, '')
                                                        ?.replace(/Baly\s*/gi, '')
                                                        ?.replace(/Gelo\s*/gi, '')
                                                        ?.trim() || 'Selecione'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-[#262626]">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCompositeOptionDialogOpen(false);
                                setCompositeOptionProduct(null);
                                setCompositeSelections({});
                            }}
                            className="w-full sm:w-auto border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={addCompositeWithSelectionsToCart}
                            className="w-full sm:w-auto bg-red-500 text-white hover:bg-red-400 font-bold"
                            disabled={compositeOptionProduct?.ingredient_groups?.some(g => g.required !== false && !compositeSelections[g.group_name])}
                        >
                            Adicionar ao Carrinho
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PDVPage;
